import { supabase } from "@/integrations/supabase/client";

export interface UserAsset {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_type: string | null;
  file_size: number | null;
  description: string | null;
  folder_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserAssetFolder {
  id: string;
  user_id: string;
  name: string;
  parent_folder_id: string | null;
  created_at: string;
  updated_at: string;
}

// ============ FOLDERS ============

export const getUserFolders = async (userId: string, parentFolderId: string | null = null): Promise<UserAssetFolder[]> => {
  let query = supabase
    .from('user_asset_folders')
    .select('*')
    .eq('user_id', userId)
    .order('name', { ascending: true });

  if (parentFolderId === null) {
    query = query.is('parent_folder_id', null);
  } else {
    query = query.eq('parent_folder_id', parentFolderId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching user folders:', error);
    throw error;
  }

  return data as UserAssetFolder[];
};

export const getAllUserFolders = async (userId: string): Promise<UserAssetFolder[]> => {
  const { data, error } = await supabase
    .from('user_asset_folders')
    .select('*')
    .eq('user_id', userId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching all user folders:', error);
    throw error;
  }

  return data as UserAssetFolder[];
};

export const getGeneralFolder = async (userId: string): Promise<UserAssetFolder | null> => {
  const { data, error } = await supabase
    .from('user_asset_folders')
    .select('*')
    .eq('user_id', userId)
    .is('parent_folder_id', null)
    .eq('name', 'Général')
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching General folder:', error);
    throw error;
  }

  return data as UserAssetFolder | null;
};

export const ensureGeneralFolder = async (userId: string): Promise<UserAssetFolder> => {
  const existingFolder = await getGeneralFolder(userId);
  
  if (existingFolder) {
    return existingFolder;
  }

  // Try to insert the folder
  const { data: newFolder, error: insertError } = await supabase
    .from('user_asset_folders')
    .insert({
      user_id: userId,
      name: 'Général',
      parent_folder_id: null
    })
    .select()
    .single();

  if (newFolder) {
    return newFolder as UserAssetFolder;
  }

  // If insert failed (likely due to unique constraint), try to fetch again
  if (insertError) {
    const refetched = await getGeneralFolder(userId);
    if (refetched) {
      return refetched;
    }
    console.error('Error ensuring General folder:', insertError);
    throw insertError;
  }

  throw new Error('Failed to create or retrieve General folder');
};

// Helper to get or create a folder for a video
export const getOrCreateVideoFolder = async (
  userId: string,
  videoId: string,
  videoTitle: string | null
): Promise<UserAssetFolder> => {
  const folderName = videoTitle || `Vidéo ${videoId.slice(0, 8)}`;
  
  // First, check if folder already exists
  const { data: existingFolder, error: findError } = await supabase
    .from('user_asset_folders')
    .select('*')
    .eq('user_id', userId)
    .eq('name', folderName)
    .is('parent_folder_id', null)
    .maybeSingle();

  if (findError) {
    console.error('Error finding video folder:', findError);
    throw findError;
  }

  if (existingFolder) {
    return existingFolder as UserAssetFolder;
  }

  // Create the folder if it doesn't exist
  const { data: newFolder, error: createError } = await supabase
    .from('user_asset_folders')
    .insert({
      user_id: userId,
      name: folderName,
      parent_folder_id: null
    })
    .select()
    .single();

  if (createError) {
    console.error('Error creating video folder:', createError);
    throw createError;
  }

  return newFolder as UserAssetFolder;
};

export const createUserFolder = async (folder: {
  user_id: string;
  name: string;
  parent_folder_id?: string | null;
}): Promise<UserAssetFolder> => {
  // First check if folder already exists (get or create pattern)
  let query = supabase
    .from('user_asset_folders')
    .select('*')
    .eq('user_id', folder.user_id)
    .eq('name', folder.name);
  
  if (folder.parent_folder_id) {
    query = query.eq('parent_folder_id', folder.parent_folder_id);
  } else {
    query = query.is('parent_folder_id', null);
  }

  const { data: existing, error: fetchError } = await query.maybeSingle();

  if (fetchError) {
    console.error('Error checking existing folder:', fetchError);
    throw fetchError;
  }

  // If folder exists, return it
  if (existing) {
    return existing as UserAssetFolder;
  }

  // Create new folder
  const { data, error } = await supabase
    .from('user_asset_folders')
    .insert(folder)
    .select()
    .single();

  if (error) {
    console.error('Error creating folder:', error);
    throw error;
  }

  return data as UserAssetFolder;
};

export const updateUserFolder = async (folderId: string, updates: { name?: string }): Promise<UserAssetFolder> => {
  const { data, error } = await supabase
    .from('user_asset_folders')
    .update(updates)
    .eq('id', folderId)
    .select()
    .single();

  if (error) {
    console.error('Error updating folder:', error);
    throw error;
  }

  return data as UserAssetFolder;
};

export const deleteUserFolder = async (folderId: string): Promise<void> => {
  const { error } = await supabase
    .from('user_asset_folders')
    .delete()
    .eq('id', folderId);

  if (error) {
    console.error('Error deleting folder:', error);
    throw error;
  }
};

// ============ ASSETS ============

export const getUserAssets = async (userId: string, folderId: string | null = null): Promise<UserAsset[]> => {
  let query = supabase
    .from('user_assets')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (folderId === null) {
    query = query.is('folder_id', null);
  } else {
    query = query.eq('folder_id', folderId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching user assets:', error);
    throw error;
  }

  return data as UserAsset[];
};

export const createUserAsset = async (asset: {
  user_id: string;
  file_name: string;
  file_path: string;
  file_type?: string;
  file_size?: number;
  description?: string;
  folder_id?: string | null;
}): Promise<UserAsset> => {
  const { data, error } = await supabase
    .from('user_assets')
    .insert(asset)
    .select()
    .single();

  if (error) {
    console.error('Error creating user asset:', error);
    throw error;
  }

  return data as UserAsset;
};

export const updateUserAsset = async (assetId: string, updates: { folder_id?: string | null; file_name?: string }): Promise<UserAsset> => {
  const { data, error } = await supabase
    .from('user_assets')
    .update(updates)
    .eq('id', assetId)
    .select()
    .single();

  if (error) {
    console.error('Error updating asset:', error);
    throw error;
  }

  return data as UserAsset;
};

export const deleteUserAsset = async (assetId: string): Promise<void> => {
  // First get the asset to get the file path
  const { data: asset, error: fetchError } = await supabase
    .from('user_assets')
    .select('file_path')
    .eq('id', assetId)
    .single();

  if (fetchError) {
    console.error('Error fetching asset for deletion:', fetchError);
    throw fetchError;
  }

  // Delete from storage
  if (asset?.file_path) {
    const { error: storageError } = await supabase.storage
      .from('user-assets')
      .remove([asset.file_path]);

    if (storageError) {
      console.error('Error deleting file from storage:', storageError);
    }
  }

  // Delete from database
  const { error } = await supabase
    .from('user_assets')
    .delete()
    .eq('id', assetId);

  if (error) {
    console.error('Error deleting user asset:', error);
    throw error;
  }
};

export const uploadAssetFile = async (
  userId: string,
  file: File
): Promise<{ filePath: string; publicUrl: string; signedUrl?: string }> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  const filePath = `${userId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('user-assets')
    .upload(filePath, file);

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw uploadError;
  }

  const { data: urlData } = supabase.storage
    .from('user-assets')
    .getPublicUrl(filePath);

  // Prefer signed URLs (works even if bucket is private); fallback to public URL.
  const { data: signedData, error: signedError } = await supabase.storage
    .from('user-assets')
    .createSignedUrl(filePath, 60 * 60);

  const signedUrl = signedError ? undefined : signedData?.signedUrl;

  return {
    filePath,
    publicUrl: urlData.publicUrl,
    signedUrl,
  };
};

export const getAssetSignedUrl = async (filePath: string): Promise<string> => {
  const { data, error } = await supabase.storage
    .from('user-assets')
    .createSignedUrl(filePath, 3600); // 1 hour expiry

  if (error) {
    console.error('Error getting signed URL:', error);
    throw error;
  }

  return data.signedUrl;
};

// Get videos that are in draft status and have no resources uploaded yet
export const getVideosWithPendingResources = async (userId: string): Promise<{
  id: string;
  title: string | null;
  status: string | null;
  hasResources: boolean;
  earliestPublicationDate: Date | null;
}[]> => {
  // Get all draft videos for user that don't have resources_not_needed flag
  const { data: videos, error: videosError } = await supabase
    .from('video_content')
    .select('id, title, status, resources_not_needed')
    .eq('user_id', userId)
    .eq('status', 'draft')
    .or('resources_not_needed.is.null,resources_not_needed.eq.false');

  if (videosError) {
    console.error('Error fetching draft videos:', videosError);
    throw videosError;
  }

  if (!videos || videos.length === 0) {
    return [];
  }

  // Get earliest publication date for each video
  const videoIds = videos.map(v => v.id);
  const { data: publications, error: pubError } = await supabase
    .from('video_content_publications')
    .select('video_content_id, scheduled_at')
    .in('video_content_id', videoIds)
    .order('scheduled_at', { ascending: true });

  if (pubError) {
    console.error('Error fetching publications:', pubError);
  }

  // Build map of video_id -> earliest publication date
  const earliestDateMap = new Map<string, Date>();
  publications?.forEach(pub => {
    if (!earliestDateMap.has(pub.video_content_id)) {
      earliestDateMap.set(pub.video_content_id, new Date(pub.scheduled_at));
    }
  });

  // Get folders that match video IDs (video folders are named with video ID pattern)
  const { data: folders, error: foldersError } = await supabase
    .from('user_asset_folders')
    .select('id, name')
    .eq('user_id', userId);

  if (foldersError) {
    console.error('Error fetching folders:', foldersError);
    throw foldersError;
  }

  // Get assets count per folder
  const { data: assets, error: assetsError } = await supabase
    .from('user_assets')
    .select('folder_id')
    .eq('user_id', userId);

  if (assetsError) {
    console.error('Error fetching assets:', assetsError);
    throw assetsError;
  }

  // Get assets linked via video comments
  const { data: commentAssets, error: commentAssetsError } = await supabase
    .from('video_comments')
    .select('video_content_id, asset_id')
    .in('video_content_id', videoIds)
    .not('asset_id', 'is', null);

  if (commentAssetsError) {
    console.error('Error fetching comment assets:', commentAssetsError);
  }

  // Build set of videos that have assets via comments
  const videosWithCommentAssets = new Set<string>();
  commentAssets?.forEach(ca => {
    if (ca.asset_id) {
      videosWithCommentAssets.add(ca.video_content_id);
    }
  });

  // Count assets per folder
  const folderAssetCount = new Map<string, number>();
  assets?.forEach(asset => {
    if (asset.folder_id) {
      folderAssetCount.set(asset.folder_id, (folderAssetCount.get(asset.folder_id) || 0) + 1);
    }
  });

  // Check which videos have folders with assets or assets via comments
  return videos.map(video => {
    // Find folder that matches this video (folder name contains video id or title)
    const matchingFolder = folders?.find(f => 
      f.name.includes(video.id) || 
      (video.title && f.name.toLowerCase().includes(video.title.toLowerCase()))
    );
    
    const hasFolderResources = matchingFolder ? (folderAssetCount.get(matchingFolder.id) || 0) > 0 : false;
    // Also check if assets are stored directly with video.id as folder_id (from ResourcesSidePanel)
    const hasDirectAssets = (folderAssetCount.get(video.id) || 0) > 0;
    const hasCommentResources = videosWithCommentAssets.has(video.id);
    const hasResources = hasFolderResources || hasDirectAssets || hasCommentResources;
    
    return {
      id: video.id,
      title: video.title,
      status: video.status,
      hasResources,
      earliestPublicationDate: earliestDateMap.get(video.id) || null
    };
  }).filter(v => !v.hasResources); // Only return videos without resources
};

// Mark a video as not needing resources
export const markVideoNoResourcesNeeded = async (videoId: string, value: boolean = true): Promise<void> => {
  const { error } = await supabase
    .from('video_content')
    .update({ resources_not_needed: value })
    .eq('id', videoId);

  if (error) {
    console.error('Error marking video as no resources needed:', error);
    throw error;
  }
};

// Mark all user's draft videos as not needing resources
export const markAllVideosNoResourcesNeeded = async (userId: string, value: boolean = true): Promise<void> => {
  const { error } = await supabase
    .from('video_content')
    .update({ resources_not_needed: value })
    .eq('user_id', userId)
    .eq('status', 'draft');

  if (error) {
    console.error('Error marking all videos as no resources needed:', error);
    throw error;
  }
};
